import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { AccountingCustomer, AccountingCustomersUpdateRequest } from '../types/models/AccountingCustomer';
import type { ApiResponseAdminAccountingCustomers } from '../types/api/ApiResponseAdminAccountingCustomers';
import { errorMessageText } from '../app/Admin/Reconciliation/reconciliationLabels';

// Staff-only WinAuditor customer codes of the « Comptabilité » ODs (docs/api-types/admin-accounting-customers.md).
// JSend at HTTP 200: branch on `body.status`, never `response.ok`. A non-staff caller gets a `fail` with
// `data.msg` (no `message`); validation errors are keyed per field (`data["customers.<accounting_key>"]`).
// The editor is driven by the entries view's own `customers[]`, so only the PATCH is needed here.

export type SaveAccountingCustomersResult =
  | { ok: true; customers: AccountingCustomer[] }
  | { ok: false; message: string; fieldErrors: Record<string, string> };

/** `PATCH /admin/accounting/customers` — merge semantics, all-or-nothing; a code is a string, `null` forgets it. */
export function useSaveAccountingCustomers(): {
  save: (body: AccountingCustomersUpdateRequest) => Promise<SaveAccountingCustomersResult>;
  saving: boolean;
} {
  const apiFetch = useApiFetch();
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (payload: AccountingCustomersUpdateRequest): Promise<SaveAccountingCustomersResult> => {
      setSaving(true);
      try {
        const response = await apiFetch('/admin/accounting/customers', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
        const body = (await response.json().catch(() => null)) as ApiResponseAdminAccountingCustomers | null;
        if (body?.status === 'success' && Array.isArray(body.data?.customers)) {
          return { ok: true, customers: body.data.customers };
        }
        const fieldErrors: Record<string, string> = {};
        for (const [key, value] of Object.entries(body?.data ?? {})) {
          if (key !== 'msg' && typeof value === 'string') {
            fieldErrors[key] = errorMessageText(value);
          }
        }
        const msg = typeof body?.data?.msg === 'string' ? body.data.msg : null;
        const raw = body?.message ?? msg;
        return {
          ok: false,
          message: raw ? errorMessageText(raw) : "Impossible d'enregistrer les codes clients WinAuditor. Réessayez.",
          fieldErrors,
        };
      } catch (err) {
        return {
          ok: false,
          message: err instanceof Error ? `Erreur réseau : ${err.message}` : 'Erreur réseau.',
          fieldErrors: {},
        };
      } finally {
        setSaving(false);
      }
    },
    [apiFetch],
  );

  return { save, saving };
}
